import { useEffect, useState } from 'react';
import {
  Plus, GripVertical, Eye, EyeOff, Trash2, Pencil, X, Check, Tag,
} from 'lucide-react';
import {
  CategoryIcon,
  SELECTABLE_ICON_KEYS,
} from '@/components/category-icon/category-icon';
import { adminFetch } from '@/api';

interface Category {
  id: string;
  name: string;
  emoji: string;
  isSystem: boolean;
  isHidden: boolean;
  sortOrder: number;
  _count: { expenses: number };
}

const ALL_EMOJI_KEYS = SELECTABLE_ICON_KEYS;

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('cart');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [error, setError] = useState('');

  const fetchCategories = async () => {
    const res = await adminFetch('/api/categories');
    const data = await res.json();
    setCategories(data.categories);
  };

  useEffect(() => { fetchCategories(); }, []);

  const toggleHidden = async (id: string, isHidden: boolean) => {
    await adminFetch('/api/categories', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, isHidden }),
    });
    fetchCategories();
  };

  const saveEdit = async (id: string) => {
    if (!editName.trim()) return;
    await adminFetch('/api/categories', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, name: editName.trim() }),
    });
    setEditId(null);
    fetchCategories();
  };

  const addCategory = async () => {
    if (!newName.trim()) return;
    setError('');
    const res = await adminFetch('/api/categories', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName.trim(), emoji: newEmoji }),
    });
    if (!res.ok) {
      const data = await res.json();
      setError(data.error || 'Failed to add');
      return;
    }
    setNewName('');
    setNewEmoji('cart');
    setShowAdd(false);
    fetchCategories();
  };

  const deleteCategory = async (id: string) => {
    setError('');
    const res = await adminFetch('/api/categories', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (!res.ok) {
      const data = await res.json();
      setError(data.error || 'Failed to delete');
      return;
    }
    fetchCategories();
  };

  const activeCount = categories.filter(c => !c.isHidden).length;
  const hiddenCount = categories.filter(c => c.isHidden).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-bold text-white tracking-tight">Categories</h1>
          <p className="text-zinc-500 text-[13px] mt-0.5">
            {activeCount} active · {hiddenCount} hidden · {categories.length} total
          </p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 text-[13px] font-semibold transition-all"
        >
          <Plus className="w-4 h-4" />
          Add Category
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-between">
          <p className="text-red-400 text-[12px] font-medium">{error}</p>
          <button onClick={() => setError('')} className="text-red-400/50 hover:text-red-400">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Add Category Form */}
      {showAdd && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 space-y-4">
          <p className="text-sm font-semibold text-white">New System Category</p>

          <div className="flex gap-4 items-start">
            {/* Icon Picker */}
            <div className="relative">
              <button
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="w-14 h-14 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center hover:border-emerald-500/30 transition-all"
              >
                <CategoryIcon emoji={newEmoji} />
              </button>
              {showIconPicker && (
                <div className="absolute top-full left-0 mt-2 z-50 bg-[#0a0a0b] border border-white/[0.08] rounded-2xl p-3 shadow-2xl w-[320px]">
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold mb-2 px-1">Select Icon</p>
                  <div className="grid grid-cols-8 gap-1.5 max-h-[200px] overflow-y-auto">
                    {ALL_EMOJI_KEYS.map((key) => (
                      <button
                        key={key}
                        onClick={() => {
                          setNewEmoji(key);
                          setShowIconPicker(false);
                        }}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                          newEmoji === key
                            ? 'ring-1 ring-emerald-400/60'
                            : 'hover:bg-white/[0.06]'
                        }`}
                        title={key}
                      >
                        <CategoryIcon emoji={key} size="sm" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Name + Submit */}
            <div className="flex-1 space-y-3">
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Category name (e.g. Insurance)"
                className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 transition-all"
                onKeyDown={e => e.key === 'Enter' && addCategory()}
              />
              <div className="flex gap-2">
                <button
                  onClick={addCategory}
                  disabled={!newName.trim()}
                  className="px-4 py-2 rounded-lg bg-emerald-500 text-black text-[12px] font-semibold hover:bg-emerald-400 disabled:opacity-40 transition-all"
                >
                  Add Category
                </button>
                <button
                  onClick={() => { setShowAdd(false); setShowIconPicker(false); }}
                  className="px-4 py-2 rounded-lg text-zinc-500 text-[12px] font-medium hover:text-zinc-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        {categories.map((cat, i) => (
          <div
            key={cat.id}
            className={`flex items-center gap-4 px-5 py-3.5 hover:bg-white/[0.02] transition-colors ${
              i < categories.length - 1 ? 'border-b border-white/[0.03]' : ''
            } ${cat.isHidden ? 'opacity-40' : ''}`}
          >
            {/* Drag handle */}
            <GripVertical className="w-4 h-4 text-zinc-700 flex-shrink-0 cursor-grab" />

            {/* Icon */}
            <CategoryIcon emoji={cat.emoji} size="sm" />

            {/* Name */}
            <div className="flex-1 min-w-0">
              {editId === cat.id ? (
                <div className="flex items-center gap-2">
                  <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="px-3 py-1 rounded-lg bg-white/[0.06] border border-white/[0.1] text-sm text-white focus:outline-none focus:border-emerald-500/30 w-full max-w-[200px]"
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveEdit(cat.id);
                      if (e.key === 'Escape') setEditId(null);
                    }}
                    autoFocus
                  />
                  <button onClick={() => saveEdit(cat.id)} className="p-1 rounded-md hover:bg-emerald-500/10">
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  </button>
                  <button onClick={() => setEditId(null)} className="p-1 rounded-md hover:bg-white/5">
                    <X className="w-3.5 h-3.5 text-zinc-500" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium text-white">{cat.name}</span>
                  <span className="text-[10px] text-zinc-600 font-[family-name:var(--font-jetbrains)]">{cat.emoji}</span>
                  {cat.isHidden && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-500/10 text-zinc-500 border border-zinc-500/20 uppercase tracking-wider font-semibold">
                      Hidden
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Expense count */}
            <div className="flex-shrink-0 text-right mr-2">
              <span className="text-[11px] text-zinc-500 font-[family-name:var(--font-jetbrains)]">
                {cat._count.expenses}
              </span>
              <span className="text-[9px] text-zinc-700 ml-1">expenses</span>
            </div>

            {/* Sort order */}
            <span className="text-[10px] text-zinc-700 font-[family-name:var(--font-jetbrains)] w-6 text-center flex-shrink-0">
              #{cat.sortOrder}
            </span>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => { setEditId(cat.id); setEditName(cat.name); }}
                className="p-1.5 rounded-lg hover:bg-white/[0.04] text-zinc-600 hover:text-zinc-300 transition-all"
                title="Rename"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => toggleHidden(cat.id, !cat.isHidden)}
                className="p-1.5 rounded-lg hover:bg-white/[0.04] text-zinc-600 hover:text-zinc-300 transition-all"
                title={cat.isHidden ? 'Show' : 'Hide'}
              >
                {cat.isHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
              {cat._count.expenses === 0 && (
                <button
                  onClick={() => deleteCategory(cat.id)}
                  className="p-1.5 rounded-lg hover:bg-red-500/10 text-zinc-600 hover:text-red-400 transition-all"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}

        {categories.length === 0 && (
          <div className="text-center py-16">
            <Tag className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No categories found</p>
          </div>
        )}
      </div>
    </div>
  );
}
